import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import Spinner from '../../Spinner';
import UserAvatar from '../../UserAvatar';

export const UserPopup = () => {
	const [activeUser, setActiveUser] = useState(null);
	const [loading, setLoading] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isVisible, setIsVisible] = useState(false);

	const popupRef = useRef(null);
	const hoverTimerRef = useRef(null);
	const leaveTimerRef = useRef(null);
	const currentUserIdRef = useRef(null);
	const cacheRef = useRef({});
	const isHoveringPopupRef = useRef(false);
	const lastHoveredMentionRef = useRef(null); // Track last hovered mention

	const timerDelay = 200; // milliseconds

	const fetchUserDetails = async (userId) => {
		if (cacheRef.current[userId]) {
			setActiveUser(cacheRef.current[userId]);
			setLoading(false);
			return;
		}

		setLoading(true);
		try {
			const res = await ApiClient.get(
				`/api/get-ms-topics-user-details/${window.MS_ID}/${window.threads.namespace}/${userId}`
			);
			cacheRef.current[userId] = res.data;
			setActiveUser(res.data);
			setLoading(false);
		} catch (err) {
			console.error(err);
			setLoading(false);
		}
	};

	useEffect(() => {
		const handleMouseEnter = (e) => {
			const el = e.target.closest?.('[data-lexical-mention-id]');
			if (!el) return;

			const userId = el.getAttribute('data-lexical-mention-id');
			if (!userId) return;

			// Store which mention we're currently hovering
			lastHoveredMentionRef.current = userId;

			clearTimeout(leaveTimerRef.current);
			clearTimeout(hoverTimerRef.current);

			const rect = el.getBoundingClientRect();
			const newPosition = {
				x: rect.left - rect.width / 2,
				y: rect.top
			};

			setPosition(newPosition);

			// If we're switching between different users
			if (currentUserIdRef.current && currentUserIdRef.current !== userId) {
				currentUserIdRef.current = userId;
				setIsVisible(true);
				setLoading(true);
				fetchUserDetails(userId);
				return;
			}

			currentUserIdRef.current = userId;

			hoverTimerRef.current = setTimeout(() => {
				// Only show if we're still hovering the same mention
				if (lastHoveredMentionRef.current === userId) {
					setIsVisible(true);
					fetchUserDetails(userId);
				}
			}, timerDelay);
		};

		const handleMouseLeave = (e) => {
			const el = e.target.closest?.('[data-lexical-mention-id]');
			if (!el) return;

			const userId = el.getAttribute('data-lexical-mention-id');
			if (!userId) return;

			// Clear the last hovered mention if we're leaving this one
			if (lastHoveredMentionRef.current === userId) {
				lastHoveredMentionRef.current = null;
			}

			clearTimeout(hoverTimerRef.current);

			// Don't do anything if we're moving to the popup
			const relatedTarget = e.relatedTarget;
			if (popupRef.current && popupRef.current.contains(relatedTarget)) {
				return;
			}

			leaveTimerRef.current = setTimeout(() => {
				if (isHoveringPopupRef.current) return;
				setIsVisible(false);
				setActiveUser(null);
				currentUserIdRef.current = null;
			}, timerDelay);
		};

		const handlePopupMouseEnter = () => {
			isHoveringPopupRef.current = true;
			clearTimeout(leaveTimerRef.current);
		};

		const handlePopupMouseLeave = (e) => {
			isHoveringPopupRef.current = false;
			
			// Check if we're moving to a mention element
			const relatedTarget = e.relatedTarget;
			if (relatedTarget && relatedTarget.closest?.('[data-lexical-mention-id]')) {
				return;
			}

			leaveTimerRef.current = setTimeout(() => {
				setIsVisible(false);
				setActiveUser(null);
				currentUserIdRef.current = null;
				lastHoveredMentionRef.current = null;
			}, timerDelay);
		};

		// Add event listeners to document
		document.addEventListener('mouseenter', handleMouseEnter, true);
		document.addEventListener('mouseleave', handleMouseLeave, true);

		return () => {
			document.removeEventListener('mouseenter', handleMouseEnter, true);
			document.removeEventListener('mouseleave', handleMouseLeave, true);
			clearTimeout(hoverTimerRef.current);
			clearTimeout(leaveTimerRef.current);
		};
	}, []);

	// Setup popup event listeners when popup is visible
	useEffect(() => {
		const popupEl = popupRef.current;
		if (!popupEl) return;

		const handlePopupMouseEnter = () => {
			isHoveringPopupRef.current = true;
			clearTimeout(leaveTimerRef.current);
		};

		const handlePopupMouseLeave = (e) => {
			isHoveringPopupRef.current = false;
			
			const relatedTarget = e.relatedTarget;
			if (relatedTarget && relatedTarget.closest?.('[data-lexical-mention-id]')) {
				return;
			}

			leaveTimerRef.current = setTimeout(() => {
				setIsVisible(false);
				setActiveUser(null);
				currentUserIdRef.current = null;
				lastHoveredMentionRef.current = null;
			}, timerDelay);
		};

		popupEl.addEventListener('mouseenter', handlePopupMouseEnter);
		popupEl.addEventListener('mouseleave', handlePopupMouseLeave);

		return () => {
			popupEl.removeEventListener('mouseenter', handlePopupMouseEnter);
			popupEl.removeEventListener('mouseleave', handlePopupMouseLeave);
		};
	}, [isVisible]);

	// Clean up timers on unmount
	useEffect(() => {
		return () => {
			clearTimeout(hoverTimerRef.current);
			clearTimeout(leaveTimerRef.current);
		};
	}, []);

	return createPortal(
		<AnimatePresence>
			{isVisible && (
				<div
					ref={popupRef}
					className="fixed z-50 text-black text-article"
					style={{
						left: `${position.x}px`,
						top: `${position.y}px`,
						pointerEvents: 'auto'
					}}
				>
					<motion.div
						className="absolute bottom-full mb-2"
						initial={{ opacity: 0, y: 10, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 10, scale: 0.95 }}
						transition={{ duration: 0.2 }}
					>
						<div className="bg-white rounded-primary border-normal border-grey p-4 w-80 overflow-hidden">
							{loading ? (
								<div className="flex items-center justify-center h-full w-full">
									<Spinner showMessage={false} size={42} />
								</div>
							) : activeUser ? (
								<div className="flex items-start space-x-4">
									<div className='rounded-full bg-white border-primary border-normal p-1'>
										<UserAvatar
											name={activeUser.full_name}
											src={activeUser.avatar_path}
											size={48}
										/>
									</div>
									<div className="flex-1 min-w-0">
										<h3 className="text-label font-semibold truncate">
											{activeUser.full_name}
										</h3>
										<p className="text-xxs text-grey-text">
											@{activeUser.user_name}
										</p>
										<div className="text-xxs text-grey-text">
											{activeUser.email}
										</div>
									</div>
								</div>
							) : null}
						</div>
					</motion.div>
				</div>
			)}
		</AnimatePresence>,
		document.body
	);
};